import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, CircularProgress } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { fetchLogs } from '../../services/logs.service';

interface SummaryCounts {
  errors: number;
  warnings: number;
  slowRequests: number;
}

export default function SummaryCards() {
  const [counts, setCounts] = useState<SummaryCounts>({ errors: 0, warnings: 0, slowRequests: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      const from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      try {
        const [errorRes, warnRes, slowRes] = await Promise.all([
          fetchLogs(`level=error&from=${from}&limit=1`) as Promise<any>,
          fetchLogs(`level=warn&from=${from}&limit=1`) as Promise<any>,
          fetchLogs(`category=slow_request&from=${from}&limit=1`) as Promise<any>,
        ]);
        setCounts({
          errors: errorRes.total ?? 0,
          warnings: warnRes.total ?? 0,
          slowRequests: slowRes.total ?? 0,
        });
      } catch (err) {
        console.error('[SummaryCards] fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  const cards = [
    {
      label: 'Critical Errors',
      sublabel: 'Last 24 hours',
      value: counts.errors,
      icon: ErrorIcon,
      iconBg: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
      cardBg: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
      valueColor: '#991B1B',
      iconColor: '#FFFFFF',
      borderColor: '#FCA5A5',
      shadowColor: 'rgba(220, 38, 38, 0.15)',
    },
    {
      label: 'Warnings',
      sublabel: 'Last 24 hours',
      value: counts.warnings,
      icon: WarningIcon,
      iconBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      cardBg: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
      valueColor: '#B45309',
      iconColor: '#FFFFFF',
      borderColor: '#FCD34D',
      shadowColor: 'rgba(245, 158, 11, 0.15)',
    },
    {
      label: 'Slow Requests',
      sublabel: 'Last 24 hours',
      value: counts.slowRequests,
      icon: SpeedIcon,
      iconBg: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
      cardBg: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
      valueColor: '#4338CA',
      iconColor: '#FFFFFF',
      borderColor: '#A5B4FC',
      shadowColor: 'rgba(99, 102, 241, 0.15)',
    },
  ];

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
      {cards.map(({ label, sublabel, value, icon: Icon, iconBg, cardBg, valueColor, iconColor, borderColor, shadowColor }) => (
        <Card
          key={label}
          sx={{
            flex: 1,
            borderRadius: '16px',
            border: `1px solid ${borderColor}`,
            boxShadow: `0 4px 12px ${shadowColor}, 0 2px 4px rgba(0,0,0,0.05)`,
            background: cardBg,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${shadowColor}, 0 4px 8px rgba(0,0,0,0.08)`,
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              {/* Icon and Label Row */}
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ 
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontSize: '0.75rem',
                      mb: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.7rem',
                      opacity: 0.7,
                    }}
                  >
                    {sublabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${shadowColor}`,
                  }}
                >
                  <Icon sx={{ color: iconColor, fontSize: 28 }} />
                </Box>
              </Stack>

              {/* Value Row */}
              <Stack direction="row" alignItems="baseline" spacing={1}>
                {loading ? (
                  <CircularProgress size={32} thickness={4} sx={{ color: valueColor }} />
                ) : (
                  <>
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      color={valueColor}
                      sx={{
                        lineHeight: 1,
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    >
                      {value.toLocaleString()}
                    </Typography>
                    {value > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: '6px',
                          bgcolor: 'rgba(0,0,0,0.05)',
                        }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 14, color: valueColor, opacity: 0.7 }} />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: valueColor, opacity: 0.7, fontSize: '0.7rem' }}
                        >
                          Active
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
